import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LinkIcon from '@mui/icons-material/Link';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import StorefrontIcon from '@mui/icons-material/Storefront';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { del, get, patch, post, put } from '../utils/api';
import { downloadEventPoster } from '../utils/posterDownload';
import PageLayout from '../components/common/PageLayout';
import EventEmailDialog from '../components/manage/EventEmailDialog';
import SponsorshipRequestDialog from '../components/manage/SponsorshipRequestDialog';

// Marketing channels shown as a status row on each event (colored = scheduled, gray = not yet).
// Keys and icons mirror CHANNEL_META in EventEmailDialog — the schedule stores these exact keys, so a
// key that drifts here silently shows as never-scheduled.
const MARKETING_CHANNELS = [
  { key: 'email', label: 'Email', Icon: MailOutlineIcon, color: '#7a1f1f' },
  { key: 'whatsapp', label: 'WhatsApp', Icon: WhatsAppIcon, color: '#25D366' },
  { key: 'facebook', label: 'Facebook', Icon: FacebookIcon, color: '#1877F2' },
  { key: 'x', label: 'X', Icon: XIcon, color: '#111111' },
  { key: 'google', label: 'Google Business', Icon: StorefrontIcon, color: '#4285F4' },
  { key: 'fb_video', label: 'FB Video', Icon: OndemandVideoIcon, color: '#1877F2' },
  { key: 'instagram', label: 'Instagram Reel', Icon: InstagramIcon, color: '#E4405F' },
  { key: 'youtube', label: 'YouTube Short', Icon: YouTubeIcon, color: '#FF0000' },
];

// A row of media-channel icons: scheduled ones in brand color, the rest grayed/blurred.
// Yodeck signage is included as a channel (colored when queued/on the TVs).
function ChannelStatusRow({ scheduled, yodeck }) {
  const set = scheduled || {};
  const off = { opacity: 0.4, filter: 'grayscale(1) blur(0.4px)' };
  const yOn = Boolean(yodeck && yodeck.enabled && (yodeck.status === 'pending' || yodeck.status === 'pushed'));
  const yTip = !yodeck ? 'Yodeck signage: not set'
    : yodeck.status === 'pushed' ? 'Yodeck signage: on the TVs'
    : yodeck.status === 'expired' ? 'Yodeck signage: ended'
    : yodeck.status === 'failed' ? 'Yodeck signage: failed'
    : yodeck.enabled ? 'Yodeck signage: queued' : 'Yodeck signage: off';
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 0.5 }}>
      {MARKETING_CHANNELS.map(({ key, label, Icon, color }) => {
        const on = Boolean(set[key]);
        return (
          <Tooltip key={key} title={`${label}: ${on ? 'scheduled' : 'not scheduled'}`}>
            <Icon sx={{ fontSize: 28, color: on ? color : 'text.disabled', verticalAlign: 'middle', ...(on ? {} : off) }} />
          </Tooltip>
        );
      })}
      <Tooltip title={yTip}>
        <Box component="img" src="/assets/images/yodeck.png" alt="Yodeck"
          sx={{ height: 22, width: 'auto', verticalAlign: 'middle', ...(yOn ? {} : off) }} />
      </Tooltip>
    </Stack>
  );
}

const DND_DEBUG = process.env.NODE_ENV === 'development';

function dndLog(step, detail) {
  if (!DND_DEBUG) return;
  try {
    console.log('[CalendarEvents][DnD]', step, detail ?? '');
  } catch {
    // ignore
  }
}

function formatEventDate(value) {
  if (!value) return '';
  try {
    return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

// Format a start..end span compactly: same month -> "Jun 24 – 28, 2026", same year ->
// "Jun 24 – Jul 2, 2026", otherwise "Dec 30, 2026 – Jan 2, 2027".
function formatDateRange(startIso, endIso) {
  try {
    const s = new Date(`${startIso}T00:00:00`);
    const e = new Date(`${endIso}T00:00:00`);
    const sameYear = s.getFullYear() === e.getFullYear();
    const sameMonth = sameYear && s.getMonth() === e.getMonth();
    if (sameMonth) {
      return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString(undefined, { day: 'numeric' })}, ${e.getFullYear()}`;
    }
    if (sameYear) {
      return `${s.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${e.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${e.getFullYear()}`;
    }
    const full = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${s.toLocaleDateString(undefined, full)} – ${e.toLocaleDateString(undefined, full)}`;
  } catch {
    return formatEventDate(startIso);
  }
}

// Latest date across a main event and all its nested sessions. A group is "multi-day" when this
// runs past the main event's own (start) date — derived purely from the sessions, no stored flag.
function getGroupEndDate(main, children) {
  let end = String(main?.event_date || '');
  // An explicit multi-day end date seeds the range even when there are no later-dated sessions.
  const explicitEnd = String(main?.event_end_date || '');
  if (explicitEnd > end) end = explicitEnd;
  const walk = (nodes) => {
    for (const node of nodes || []) {
      const d = String(node.event_date || '');
      if (d > end) end = d;
      if (node.children?.length) walk(node.children);
    }
  };
  walk(children);
  return end;
}

function getProductLinkHref(row) {
  return String(row?.product_url || '').trim();
}

function openProductLink(event, href) {
  event.stopPropagation();
  if (!href) return;
  window.open(href, '_blank', 'noopener,noreferrer');
}

function buildDisplayRows(events) {
  const mains = events
    .filter((event) => event.is_primary_event)
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
  const sessions = events.filter((event) => !event.is_primary_event);
  const rows = [];
  const used = new Set();

  for (const main of mains) {
    rows.push(main);
    used.add(main.event_key);
    const children = sessions
      .filter((session) => String(session.parent_event_key) === String(main.event_key))
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    for (const child of children) {
      rows.push(child);
      used.add(child.event_key);
    }
  }

  for (const session of sessions.filter((item) => !used.has(item.event_key))) {
    rows.push(session);
  }
  return rows;
}

function buildEventGroups(events) {
  const mains = events
    .filter((event) => event.is_primary_event)
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
  const sessions = events.filter((event) => !event.is_primary_event);
  const eventByKey = Object.fromEntries(events.map((event) => [String(event.event_key), event]));
  const childrenByParent = new Map();

  for (const session of sessions) {
    const rawParentKey = String(session.parent_event_key || '').trim();
    const parentKey = rawParentKey && eventByKey[rawParentKey] ? rawParentKey : '__ungrouped__';
    const current = childrenByParent.get(parentKey) || [];
    current.push(session);
    childrenByParent.set(parentKey, current);
  }

  for (const [key, current] of childrenByParent.entries()) {
    childrenByParent.set(
      key,
      current.sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    );
  }

  const visited = new Set();
  const buildChildren = (parentKey, trail = new Set()) => {
    const childRows = childrenByParent.get(String(parentKey || '')) || [];
    return childRows.map((child) => {
      const childKey = String(child.event_key);
      if (trail.has(childKey)) {
        return { ...child, children: [] };
      }
      visited.add(childKey);
      const nextTrail = new Set(trail);
      nextTrail.add(childKey);
      return {
        ...child,
        children: buildChildren(childKey, nextTrail),
      };
    });
  };

  const groups = mains.map((main) => ({
    main,
    children: buildChildren(main.event_key, new Set([String(main.event_key)])),
  }));

  const ungrouped = buildChildren('__ungrouped__')
    .concat(
      sessions
        .filter((session) => !visited.has(String(session.event_key)))
        .map((session) => ({ ...session, children: [] }))
    )
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));

  return { groups, ungrouped };
}

export default function ManageCalendarEventsPage() {
  const [rows, setRows] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [productInput, setProductInput] = React.useState('');
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [selectedEvent, setSelectedEvent] = React.useState(null);
  const [moveDialogOpen, setMoveDialogOpen] = React.useState(false);
  const [moveEvent, setMoveEvent] = React.useState(null);
  const [moveTarget, setMoveTarget] = React.useState(null);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editEvent, setEditEvent] = React.useState(null);
  const [editForm, setEditForm] = React.useState({ title: '', event_date: '', event_end_date: '', event_time: '', event_page_link: '' });
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);
  const [addForm, setAddForm] = React.useState({ title: '', event_date: '', event_end_date: '', event_time: '', event_page_link: '', description: '', is_primary_event: true, parent: null, parentLocked: false, product: null, sessions: [] });
  const [addProducts, setAddProducts] = React.useState([]);
  const [addProductInput, setAddProductInput] = React.useState('');
  const [showPast, setShowPast] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [ok, setOk] = React.useState('');
  const [emailEvent, setEmailEvent] = React.useState(null);
  const [sponsorReqEvent, setSponsorReqEvent] = React.useState(null);
  const [actionsAnchor, setActionsAnchor] = React.useState(null);
  const [actionsRow, setActionsRow] = React.useState(null);
  const openActions = (e, row) => { setActionsAnchor(e.currentTarget); setActionsRow(row); };
  const closeActions = () => { setActionsAnchor(null); setActionsRow(null); };
  const runAction = (fn) => { closeActions(); fn(); };
  const [posterBusy, setPosterBusy] = React.useState('');

  // Download the share-ready event poster PNG (rendered client-side; no server Chromium).
  const downloadPoster = async (row) => {
    if (!row?.event_key) return;
    setPosterBusy(row.event_key); setError(''); setOk('');
    try {
      await downloadEventPoster(row.event_key);
      setOk(`Poster image downloaded for "${row.title}".`);
    } catch (e) { setError(e?.message || 'Failed to download poster'); }
    finally { setPosterBusy(''); }
  };
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dragSessionKey, setDragSessionKey] = React.useState('');
  const [dropTargetKey, setDropTargetKey] = React.useState('');
  const [dragPreview, setDragPreview] = React.useState(null);
  const dragSessionRef = React.useRef('');
  const dropTargetRef = React.useRef('');
  const mainRowRefs = React.useRef(new Map());
  /** Last pointer position during drag (hit-test fallback on pointerup) */
  const lastPointerRef = React.useRef({ x: 0, y: 0 });
  const dragListenersRef = React.useRef({ move: null, up: null, cancel: null });

  const [yodeckMap, setYodeckMap] = React.useState({}); // event_key -> yodeck record
  const [channelMap, setChannelMap] = React.useState({}); // event_key -> { email, whatsapp, ... } scheduled
  const [expandedSessions, setExpandedSessions] = React.useState({}); // event_key -> bool (collapsed by default)
  const toggleSessions = (key) => setExpandedSessions((m) => ({ ...m, [key]: !m[key] }));
  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await get('/api/manage/calendar-events');
      const events = Array.isArray(res?.events) ? res.events : [];
      dndLog('load ok', {
        count: events.length,
        sessions: events
          .filter((e) => !e.is_primary_event)
          .map((e) => ({ key: e.event_key, parent: e.parent_event_key, custom: e.parent_is_custom })),
      });
      setRows(events);
      // Yodeck signage status per event (one fetch) — best-effort.
      try {
        const yp = await get('/api/manage/yodeck-posts');
        const map = {};
        (yp?.posts || []).forEach((p) => { map[p.event_key] = p; });
        setYodeckMap(yp?.enabled ? map : {});
      } catch { setYodeckMap({}); }
      // Scheduled marketing channels per event (union across its active schedules) — best-effort.
      try {
        const es = await get('/api/manage/email-schedules');
        const cmap = {};
        (es?.schedules || []).forEach((s) => {
          if (['cancelled', 'canceled', 'failed'].includes(String(s.status))) return;
          const chs = (Array.isArray(s.channels) && s.channels.length ? s.channels : ['email']).map(String);
          const set = cmap[s.event_key] || (cmap[s.event_key] = {});
          chs.forEach((c) => { set[c] = true; });
        });
        setChannelMap(cmap);
      } catch { setChannelMap({}); }
    } catch (e) {
      dndLog('load failed', e?.message || e);
      setError(e?.message || 'Failed to load calendar events');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    if (!dialogOpen || !productInput || productInput.length < 2) {
      setProducts([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/products', { q: productInput, limit: 50 });
        if (!ignore) setProducts(Array.isArray(res?.products) ? res.products : []);
      } catch {
        if (!ignore) setProducts([]);
      }
    })();
    return () => { ignore = true; };
  }, [dialogOpen, productInput]);

  // Product search for the Add-event dialog (independent of the Map-product dialog).
  React.useEffect(() => {
    if (!addDialogOpen || !addProductInput || addProductInput.length < 2) {
      setAddProducts([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/products', { q: addProductInput, limit: 50 });
        if (!ignore) setAddProducts(Array.isArray(res?.products) ? res.products : []);
      } catch {
        if (!ignore) setAddProducts([]);
      }
    })();
    return () => { ignore = true; };
  }, [addDialogOpen, addProductInput]);

  const displayRows = React.useMemo(() => buildDisplayRows(rows), [rows]);
  const groupedRows = React.useMemo(() => buildEventGroups(rows), [rows]);

  // Today's date as 'YYYY-MM-DD' in local time (en-CA yields ISO order). Dates are ISO strings, so
  // plain string comparison is a correct date comparison.
  const todayIso = React.useMemo(() => {
    try { return new Date().toLocaleDateString('en-CA'); } catch { return ''; }
  }, []);

  // A group is "past" when its latest date (main event or any nested session) is before today.
  const isPastGroup = React.useCallback((group) => {
    let maxDate = String(group?.main?.event_date || '');
    const walk = (nodes) => {
      for (const node of nodes || []) {
        const d = String(node.event_date || '');
        if (d > maxDate) maxDate = d;
        if (node.children?.length) walk(node.children);
      }
    };
    walk(group?.children);
    return Boolean(maxDate) && Boolean(todayIso) && maxDate < todayIso;
  }, [todayIso]);

  const pastGroups = React.useMemo(
    () => groupedRows.groups.filter(isPastGroup),
    [groupedRows.groups, isPastGroup],
  );
  const visibleGroups = showPast
    ? groupedRows.groups
    : groupedRows.groups.filter((group) => !isPastGroup(group));
  const mainEventOptions = React.useMemo(
    () => rows.filter((row) => row.is_primary_event),
    [rows],
  );

  const assignSessionParent = React.useCallback(async (sessionKey, parentEventKey) => {
    dndLog('assign called', { sessionKey, parentEventKey, ref: dragSessionRef.current });
    if (!sessionKey || !parentEventKey) {
      dndLog('assign aborted — missing keys', { sessionKey, parentEventKey });
      dragSessionRef.current = '';
      dropTargetRef.current = '';
      setDragPreview(null);
      setDragSessionKey('');
      setDropTargetKey('');
      return;
    }
    const session = rows.find((row) => row.event_key === sessionKey);
    const parent = rows.find((row) => row.event_key === parentEventKey);
    if (session?.parent_event_key === parentEventKey) {
      dndLog('assign skipped — already under parent', {
        session: session?.title,
        parent: parent?.title,
        parent_event_key: parentEventKey,
      });
      dragSessionRef.current = '';
      dropTargetRef.current = '';
      setDragPreview(null);
      setDragSessionKey('');
      setDropTargetKey('');
      return;
    }

    setSaving(true);
    setError('');
    setOk('');
    const url = `/api/manage/calendar-events/${encodeURIComponent(String(sessionKey))}/session-parent`;
    const body = { parent_event_key: parentEventKey };
    dndLog('assign PUT', { url, body });
    try {
      const res = await put(url, body);
      dndLog('assign PUT ok', res);
      setOk(`Moved "${session?.title || 'session'}" under "${parent?.title || 'main event'}".`);
      await load();
    } catch (e) {
      dndLog('assign PUT failed', {
        message: e?.message,
        status: e?.status,
        data: e?.data,
        url: e?.url,
      });
      setError(e?.message || 'Failed to move session');
    } finally {
      setSaving(false);
      dragSessionRef.current = '';
      dropTargetRef.current = '';
      setDragPreview(null);
      setDragSessionKey('');
      setDropTargetKey('');
    }
  }, [load, rows]);

  const removeDocumentDragListeners = React.useCallback(() => {
    const { move, up, cancel } = dragListenersRef.current;
    if (move) document.removeEventListener('pointermove', move);
    if (up) document.removeEventListener('pointerup', up);
    if (cancel) document.removeEventListener('pointercancel', cancel);
    dragListenersRef.current = { move: null, up: null, cancel: null };
  }, []);

  const cancelDrag = React.useCallback(() => {
    dndLog('drag cancel');
    removeDocumentDragListeners();
    dragSessionRef.current = '';
    dropTargetRef.current = '';
    setDragPreview(null);
    setDragSessionKey('');
    setDropTargetKey('');
  }, [removeDocumentDragListeners]);
  const openMapDialog = (row) => {
    setSelectedEvent(row);
    setSelectedProduct(row?.product_id ? {
      id: Number(row.product_id),
      name: row.product_name || `Product #${row.product_id}`,
      sku: row.product_sku || '',
    } : null);
    setProductInput(row?.product_name || '');
    setDialogOpen(true);
    setError('');
    setOk('');
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setSelectedEvent(null);
    setSelectedProduct(null);
    setProductInput('');
    setProducts([]);
  };

  const openMoveDialog = (row) => {
    setMoveEvent(row);
    setMoveTarget(null);
    setMoveDialogOpen(true);
    setError('');
    setOk('');
  };

  const closeMoveDialog = () => {
    if (saving) return;
    setMoveDialogOpen(false);
    setMoveEvent(null);
    setMoveTarget(null);
  };

  const openEditDialog = (row) => {
    setEditEvent(row);
    setEditForm({
      title: row?.title || '',
      event_date: row?.event_date || '',
      event_end_date: row?.event_end_date || '',
      event_time: row?.event_time || '',
      event_page_link: row?.event_page_link || '',
    });
    setEditDialogOpen(true);
    setError('');
    setOk('');
  };

  const closeEditDialog = () => {
    if (saving) return;
    setEditDialogOpen(false);
    setEditEvent(null);
    setEditForm({ title: '', event_date: '', event_end_date: '', event_time: '', event_page_link: '' });
  };

  const onSaveEdit = async () => {
    if (!editEvent?.event_key) return;
    const title = String(editForm.title || '').trim();
    if (!title) {
      setError('Title cannot be empty.');
      return;
    }
    const rawTime = String(editForm.event_time || '').trim();
    const rawPageLink = String(editForm.event_page_link || '').trim();
    const rawDate = String(editForm.event_date || '').trim();
    const rawEndDate = String(editForm.event_end_date || '').trim();
    if (rawDate && !/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
      setError('Enter a valid date.');
      return;
    }
    if (rawEndDate) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(rawEndDate)) { setError('Enter a valid end date.'); return; }
      if (rawDate && rawEndDate < rawDate) { setError('End date cannot be before the start date.'); return; }
    }
    const body = {
      title,
      event_time: rawTime || null,
      event_page_link: rawPageLink || null,
    };
    // Only send the date when it actually changed, so unchanged saves don't re-slot the list.
    if (rawDate && rawDate !== String(editEvent.event_date || '')) {
      body.event_date = rawDate;
    }
    // End date only applies to main events; send it (null clears) so multi-day can be set/removed.
    if (editEvent.is_primary_event) {
      body.event_end_date = rawEndDate || null;
    }
    setSaving(true);
    setError('');
    setOk('');
    try {
      await patch(`/api/manage/calendar-events/${encodeURIComponent(String(editEvent.event_key))}`, body);
      setOk(`Updated "${title}".`);
      setEditDialogOpen(false);
      setEditEvent(null);
      setEditForm({ title: '', event_date: '', event_end_date: '', event_time: '', event_page_link: '' });
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to update calendar event');
    } finally {
      setSaving(false);
    }
  };

  // Inline edit of a MAIN event on its parent card (replaces the Edit-event dialog for main events).
  // Title (+ link) always; date/end-date/time only for sessionless events — session events derive dates
  // from their sessions, so those fields are read-only there.
  const [editingMainKey, setEditingMainKey] = React.useState(null);
  const [mainForm, setMainForm] = React.useState({ title: '', event_date: '', event_end_date: '', event_time: '', event_page_link: '' });
  const startMainEdit = (m) => {
    setEditingMainKey(m.event_key);
    setMainForm({ title: m.title || '', event_date: m.event_date || '', event_end_date: m.event_end_date || '', event_time: m.event_time || '', event_page_link: m.event_page_link || '' });
    setError(''); setOk('');
  };
  const cancelMainEdit = () => setEditingMainKey(null);
  const saveMainEdit = async (m, hasSessions) => {
    const title = String(mainForm.title || '').trim();
    if (!title) { setError('Title cannot be empty.'); return; }
    const body = { title, event_page_link: String(mainForm.event_page_link || '').trim() || null };
    if (!hasSessions) {
      const rawDate = String(mainForm.event_date || '').trim();
      const rawEnd = String(mainForm.event_end_date || '').trim();
      if (rawDate && !/^\d{4}-\d{2}-\d{2}$/.test(rawDate)) { setError('Enter a valid date.'); return; }
      if (rawEnd) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(rawEnd)) { setError('Enter a valid end date.'); return; }
        if (rawDate && rawEnd < rawDate) { setError('End date cannot be before the start date.'); return; }
      }
      body.event_time = String(mainForm.event_time || '').trim() || null;
      if (rawDate && rawDate !== String(m.event_date || '')) body.event_date = rawDate;
      body.event_end_date = rawEnd || null;
    }
    setSaving(true); setError(''); setOk('');
    try {
      await patch(`/api/manage/calendar-events/${encodeURIComponent(String(m.event_key))}`, body);
      setOk(`Updated "${title}".`);
      setEditingMainKey(null);
      await load();
    } catch (e) { setError(e?.message || 'Failed to update calendar event'); } finally { setSaving(false); }
  };

  const openAddDialog = (preset = null) => {
    // preset (from a main event's "Add session" button) opens the dialog in session mode with the
    // parent locked and the date defaulted to the main event's date.
    setAddForm({
      title: '',
      event_date: preset?.parent?.event_date || '',
      event_end_date: '',
      event_time: '',
      event_page_link: '',
      description: '',
      is_primary_event: preset ? false : true,
      parent: preset?.parent || null,
      parentLocked: Boolean(preset?.parent),
      product: null,
      sessions: [],
    });
    setAddProductInput('');
    setAddProducts([]);
    setError('');
    setOk('');
    setAddDialogOpen(true);
  };

  const addSessionRow = () => setAddForm((f) => ({
    ...f,
    sessions: [...f.sessions, { title: '', event_date: f.event_date || '', event_time: '' }],
  }));
  const updateSessionRow = (idx, patchObj) => setAddForm((f) => ({
    ...f,
    sessions: f.sessions.map((s, i) => (i === idx ? { ...s, ...patchObj } : s)),
  }));
  const removeSessionRow = (idx) => setAddForm((f) => ({
    ...f,
    sessions: f.sessions.filter((_, i) => i !== idx),
  }));

  const closeAddDialog = () => {
    if (saving) return;
    setAddDialogOpen(false);
  };

  const onCreateEvent = async () => {
    const title = String(addForm.title || '').trim();
    const eventDate = String(addForm.event_date || '').trim();
    const isMain = Boolean(addForm.is_primary_event);
    if (!title) {
      setError('Title is required.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(eventDate)) {
      setError('Pick a valid event date.');
      return;
    }
    if (!isMain && !addForm.parent?.event_key) {
      setError('Choose the main event this session nests under.');
      return;
    }
    const endDate = isMain ? String(addForm.event_end_date || '').trim() : '';
    if (endDate) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(endDate)) { setError('Enter a valid end date.'); return; }
      if (endDate < eventDate) { setError('End date cannot be before the start date.'); return; }
    }
    // Validate inline session rows (main events only).
    const sessionRows = isMain
      ? addForm.sessions
        .map((s) => ({
          title: String(s.title || '').trim(),
          event_date: String(s.event_date || '').trim() || eventDate,
          event_time: String(s.event_time || '').trim(),
        }))
        .filter((s) => s.title || s.event_time)
      : [];
    for (const s of sessionRows) {
      if (!s.title) { setError('Every session needs a title (or remove the empty row).'); return; }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(s.event_date)) { setError(`Session "${s.title}" has an invalid date.`); return; }
    }
    setSaving(true);
    setError('');
    setOk('');
    try {
      const created = await post('/api/manage/calendar-events', {
        title,
        event_date: eventDate,
        event_end_date: endDate || null,
        event_time: String(addForm.event_time || '').trim() || null,
        event_page_link: String(addForm.event_page_link || '').trim() || null,
        is_primary_event: isMain,
        parent_event_key: !isMain && addForm.parent ? String(addForm.parent.event_key) : null,
      });
      const newKey = created?.event_key;
      const warnings = [];
      if (isMain && newKey) {
        // Map product (main events only).
        if (addForm.product?.id) {
          try {
            await put(`/api/manage/calendar-events/${encodeURIComponent(String(newKey))}/product-mapping`, { product_id: Number(addForm.product.id) });
          } catch (e) { warnings.push(`product mapping (${e?.message || 'failed'})`); }
        }
        // Save the newsletter "Importance" description (email content).
        if (String(addForm.description || '').trim()) {
          try {
            await put(`/api/manage/calendar-events/${encodeURIComponent(String(newKey))}/email-content`, { description: String(addForm.description).trim() });
          } catch (e) { warnings.push(`description (${e?.message || 'failed'})`); }
        }
        // Create inline sessions under the new main event.
        for (const s of sessionRows) {
          try {
            await post('/api/manage/calendar-events', {
              title: s.title,
              event_date: s.event_date,
              event_time: s.event_time || null,
              is_primary_event: false,
              parent_event_key: String(newKey),
            });
          } catch (e) { warnings.push(`session "${s.title}" (${e?.message || 'failed'})`); }
        }
      }
      const sessionNote = sessionRows.length ? ` with ${sessionRows.length} session(s)` : '';
      setOk(warnings.length
        ? `Created "${title}"${sessionNote}, but some steps failed: ${warnings.join('; ')}.`
        : `Created "${title}"${sessionNote}.`);
      setAddDialogOpen(false);
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  const onDeleteEvent = async (row) => {
    if (!row?.event_key) return;
    const label = row.is_primary_event ? 'main event' : 'session';
    if (!window.confirm(
      `Delete ${label} "${row.title}"? This permanently removes it and cannot be undone.\n\n`
      + 'If the event is just cancelled or postponed, use the visibility toggle to hide it instead — that keeps the record.'
    )) return;
    setSaving(true);
    setError('');
    setOk('');
    try {
      await del(`/api/manage/calendar-events/${encodeURIComponent(String(row.event_key))}`);
      setOk(`Deleted "${row.title}".`);
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to delete event');
    } finally {
      setSaving(false);
    }
  };

  // Flag/unflag a MAJOR event — announced ~2 months early on Yodeck + featured in the weekly digest.
  const onToggleMajor = async (row) => {
    if (!row?.event_key) return;
    const nextMajor = !row.is_major_event;
    setSaving(true); setError(''); setOk('');
    try {
      await patch(`/api/manage/calendar-events/${encodeURIComponent(String(row.event_key))}`, { is_major_event: nextMajor });
      setOk(nextMajor ? `Flagged "${row.title}" as a major event (announced ~2 months early).` : `Removed major flag from "${row.title}".`);
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to update major flag');
    } finally {
      setSaving(false);
    }
  };

  const onToggleHidden = async (row) => {
    if (!row?.event_key) return;
    const nextHidden = !row.is_hidden;
    setSaving(true);
    setError('');
    setOk('');
    try {
      await patch(`/api/manage/calendar-events/${encodeURIComponent(String(row.event_key))}`, {
        is_hidden: nextHidden,
      });
      setOk(`${nextHidden ? 'Hidden' : 'Unhidden'} "${row.title}".`);
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to update visibility');
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    if (!selectedEvent?.event_key) {
      setError('Select an event.');
      return;
    }
    if (!selectedProduct?.id) {
      setError('Select a product.');
      return;
    }
    setSaving(true);
    setError('');
    setOk('');
    try {
      await put(`/api/manage/calendar-events/${encodeURIComponent(String(selectedEvent.event_key))}/product-mapping`, {
        product_id: Number(selectedProduct.id),
      });
      setOk(`Mapped "${selectedEvent.title}" to "${selectedProduct.name}".`);
      closeDialog();
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to save calendar event mapping');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Remove product mapping from "${row.title}"?`)) return;
    setError('');
    setOk('');
    try {
      await del(`/api/manage/calendar-events/${encodeURIComponent(String(row.event_key))}/product-mapping`);
      setOk(`Removed product mapping from "${row.title}".`);
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to remove mapping');
    }
  };

  const onConvertToSession = React.useCallback(async (row) => {
    if (!row?.event_key) return;
    const mappingNote = row.product_id ? ' This will also remove its current product mapping.' : '';
    if (!window.confirm(`Convert "${row.title}" to a session so it can be dragged under another main event?${mappingNote}`)) {
      return;
    }
    setSaving(true);
    setError('');
    setOk('');
    try {
      await put(`/api/manage/calendar-events/${encodeURIComponent(String(row.event_key))}/convert-to-session`, {});
      setOk(`Converted "${row.title}" to a session. You can now drag it under another main event.`);
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to convert main event to session');
    } finally {
      setSaving(false);
    }
  }, [load]);

  const onMoveToMainEvent = React.useCallback(async () => {
    if (!moveEvent?.event_key) {
      setError('Select an event to move.');
      return;
    }
    if (!moveTarget?.event_key) {
      setError('Select a destination main event.');
      return;
    }
    if (String(moveEvent.event_key) === String(moveTarget.event_key)) {
      setError('Choose a different main event.');
      return;
    }
    setSaving(true);
    setError('');
    setOk('');
    try {
      if (moveEvent.is_primary_event) {
        await put(`/api/manage/calendar-events/${encodeURIComponent(String(moveEvent.event_key))}/convert-to-session`, {
          parent_event_key: moveTarget.event_key,
        });
        setOk(`Converted "${moveEvent.title}" to a session under "${moveTarget.title}".`);
      } else {
        await put(`/api/manage/calendar-events/${encodeURIComponent(String(moveEvent.event_key))}/session-parent`, {
          parent_event_key: moveTarget.event_key,
        });
        setOk(`Moved "${moveEvent.title}" under "${moveTarget.title}".`);
      }
      closeMoveDialog();
      await load();
    } catch (e) {
      setError(e?.message || 'Failed to move event');
    } finally {
      setSaving(false);
    }
  }, [closeMoveDialog, load, moveEvent, moveTarget]);

  // Pointer capture on <tr> keeps all events on the session row, so pointerEnter never fires on mains.
  // Use document pointermove + elementFromPoint to find the drop target.
  React.useEffect(() => () => {
    removeDocumentDragListeners();
  }, [removeDocumentDragListeners]);

  const setMainRowRef = React.useCallback((eventKey, node) => {
    const key = String(eventKey || '');
    if (!key) return;
    if (node) {
      mainRowRefs.current.set(key, node);
    } else {
      mainRowRefs.current.delete(key);
    }
  }, []);

  const hitTestDropMain = React.useCallback((clientX, clientY) => {
    const mainRows = [];
    for (const [eventKey, node] of mainRowRefs.current.entries()) {
      if (!node?.isConnected) continue;
      const rect = node.getBoundingClientRect();
      mainRows.push({ eventKey, rect });
      const insideX = clientX >= rect.left && clientX <= rect.right;
      const insideY = clientY >= rect.top && clientY <= rect.bottom;
      if (insideX && insideY) {
        return eventKey;
      }
    }

    let nearestKey = '';
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (const { eventKey, rect } of mainRows) {
      const centerY = rect.top + (rect.height / 2);
      const distance = Math.abs(clientY - centerY);
      if (distance < nearestDistance) {
        nearestKey = eventKey;
        nearestDistance = distance;
      }
    }
    if (nearestKey && nearestDistance <= 56) {
      return nearestKey;
    }

    const el = document.elementFromPoint(clientX, clientY);
    if (!el) return '';
    const tr = el.closest('[data-cal-main-key]');
    return tr?.getAttribute('data-cal-main-key') || '';
  }, []);

  const handleSessionPointerDown = (event, row) => {
    if (saving || event.button !== 0) return;
    event.preventDefault();
    window.getSelection?.()?.removeAllRanges?.();
    removeDocumentDragListeners();

    const key = String(row.event_key);
    dragSessionRef.current = key;
    dropTargetRef.current = '';
    setDragSessionKey(key);
    setDropTargetKey('');
    setDragPreview({
      title: row.title,
      x: event.clientX,
      y: event.clientY,
    });
    dndLog('pointerDown', { sessionKey: key, title: row.title });

    const onMove = (ev) => {
      if (!dragSessionRef.current) return;
      lastPointerRef.current = { x: ev.clientX, y: ev.clientY };
      setDragPreview((current) => (current ? {
        ...current,
        x: ev.clientX,
        y: ev.clientY,
      } : current));
      const parentKey = hitTestDropMain(ev.clientX, ev.clientY);
      if (parentKey !== dropTargetRef.current) {
        dropTargetRef.current = parentKey;
        setDropTargetKey(parentKey);
        if (parentKey) {
          const title = rows.find((r) => String(r.event_key) === parentKey)?.title;
          dndLog('hit main', { parentKey, title });
        }
      }
    };

    const finish = (ev) => {
      removeDocumentDragListeners();
      const sessionKey = dragSessionRef.current;
      const clientX = ev?.clientX ?? lastPointerRef.current.x;
      const clientY = ev?.clientY ?? lastPointerRef.current.y;
      const parentFromRef = dropTargetRef.current;
      const parentFromPoint = hitTestDropMain(clientX, clientY);
      const parentKey = parentFromRef || parentFromPoint;

      dndLog('pointer finish', {
        sessionKey,
        parentKey,
        parentFromRef,
        parentFromPoint,
        targetTitle: rows.find((r) => String(r.event_key) === parentKey)?.title,
      });

      if (sessionKey && parentKey) {
        assignSessionParent(sessionKey, parentKey);
      } else {
        cancelDrag();
      }
    };

    const onUp = (ev) => finish(ev);
    const onCancel = () => {
      dndLog('pointerCancel');
      cancelDrag();
    };

    dragListenersRef.current = { move: onMove, up: onUp, cancel: onCancel };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);

    lastPointerRef.current = { x: event.clientX, y: event.clientY };
    onMove(event);
  };

  const mappedMainCount = rows.filter((row) => row.is_primary_event && row.product_id).length;
  const totalMainCount = rows.filter((row) => row.is_primary_event).length;
  const moveTargetOptions = rows.filter(
    (row) => row.is_primary_event && String(row.event_key) !== String(moveEvent?.event_key || '')
  );

  const renderSessionNode = (row, depth = 0) => {
    const isDragging = dragSessionKey === row.event_key;
    const indent = { xs: 0, md: 3 + (depth * 2) };

    return (
      <Stack key={row.event_key} spacing={1}>
        <Box
          onPointerDown={(e) => handleSessionPointerDown(e, row)}
          sx={{
            ml: indent,
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            border: '1px solid',
            borderColor: isDragging ? 'success.main' : (row.is_hidden ? '#f2c97d' : '#ececec'),
            bgcolor: isDragging ? '#f2fbf4' : (row.is_hidden ? '#fffaf0' : '#fff'),
            opacity: isDragging ? 0.35 : (row.is_hidden ? 0.7 : 1),
            cursor: !saving ? 'grab' : 'default',
            touchAction: 'none',
            userSelect: isDragging ? 'none' : 'auto',
            '&:active': !saving ? { cursor: 'grabbing' } : {},
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <Box sx={{ color: 'text.disabled', mt: 0.2 }}>
                <DragIndicatorIcon fontSize="small" />
              </Box>
              <Stack spacing={0.35} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {row.title}
                  </Typography>
                  <Chip
                    size="small"
                    label={row.original_is_primary_event ? 'Converted session' : 'Session'}
                    variant="outlined"
                  />
                  {row.is_hidden ? (
                    <Chip size="small" label="Hidden" color="warning" variant="outlined" />
                  ) : null}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {formatEventDate(row.event_date)} • {row.event_day}{row.event_time ? ` • ${row.event_time}` : ''}
                </Typography>
                {row.product_id ? (
                  <Typography variant="caption" color="success.dark">
                    {row.product_inherited ? 'Product (from main event): ' : 'Product: '}
                    {row.product_name || `#${row.product_id}`}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                size="small"
                disabled={saving}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => openEditDialog(row)}
                aria-label={`Edit ${row.title}`}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <Tooltip title="Delete this session">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={saving}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => onDeleteEvent(row)}
                    aria-label={`Delete ${row.title}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={row.is_hidden ? 'Hidden (e.g. cancelled) — click to show on portal' : 'Visible on portal — click to hide (use for cancelled/postponed events)'}>
                <Switch
                  size="small"
                  color="success"
                  checked={!row.is_hidden}
                  disabled={saving}
                  onPointerDown={(e) => e.stopPropagation()}
                  onChange={() => onToggleHidden(row)}
                  inputProps={{ 'aria-label': row.is_hidden ? `Show ${row.title}` : `Hide ${row.title}` }}
                />
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                disabled={saving}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => openMoveDialog(row)}
              >
                Move to...
              </Button>
              {row.parent_is_custom && row.default_parent_event_key ? (
                <Button
                  size="small"
                  variant="text"
                  disabled={saving}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => assignSessionParent(row.event_key, row.default_parent_event_key)}
                >
                  Reset
                </Button>
              ) : null}
            </Stack>
          </Stack>
        </Box>
        {Array.isArray(row.children) && row.children.length > 0 ? row.children.map((child) => renderSessionNode(child, depth + 1)) : null}
      </Stack>
    );
  };

  return (
    <PageLayout contentMaxWidth="lg" showHeaderImage={false} showOrgHeader>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Calendar Events &amp; Marketing</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            size="small"
            color="secondary"
            variant="outlined"
            label={`${mappedMainCount} of ${totalMainCount} main events mapped`}
          />
          <Button variant="outlined" href="/manage/scheduled-emails">Scheduled emails</Button>
          <Button variant="outlined" onClick={load} disabled={loading || saving}>Refresh</Button>
          <Button variant="contained" onClick={() => openAddDialog()} disabled={loading || saving}>Add event</Button>
        </Stack>
      </Stack>

      {error ? <Typography color="error" sx={{ mb: 1.5 }}>{error}</Typography> : null}
      {ok ? <Typography color="success.main" sx={{ mb: 1.5 }}>{ok}</Typography> : null}

      <Stack spacing={2}>
        {!loading && displayRows.length === 0 ? (
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid #eee', p: 2 }}>
            <Typography>No calendar events found.</Typography>
          </Box>
        ) : null}

        {pastGroups.length > 0 ? (
          <Box>
            <Button size="small" variant="text" onClick={() => setShowPast((v) => !v)}>
              {showPast
                ? `Hide ${pastGroups.length} past event${pastGroups.length > 1 ? 's' : ''}`
                : `Show ${pastGroups.length} past event${pastGroups.length > 1 ? 's' : ''}`}
            </Button>
          </Box>
        ) : null}

        {visibleGroups.map(({ main, children }) => {
          const isDropTarget = dropTargetKey === main.event_key && Boolean(dragSessionKey);
          const productHref = getProductLinkHref(main);
          // Dates come from the server's shared resolver (lib/eventFacts.js) so this page prints the
          // SAME range as the event email / WhatsApp caption / weekly digest. Falls back to the local
          // derivation only if an older payload arrives without `dates`.
          const groupEndDate = main.dates?.end || getGroupEndDate(main, children);
          const isMultiDay = main.dates ? main.dates.isMultiDay : (Boolean(groupEndDate) && groupEndDate > String(main.event_date || ''));
          const mainDateLabel = main.dates
            ? (main.dates.isMultiDay ? main.dates.rangeText : `${formatEventDate(main.event_date)} • ${main.event_day}`)
            : (isMultiDay
              ? formatDateRange(main.event_date, groupEndDate)
              : `${formatEventDate(main.event_date)} • ${main.event_day}`);
          const hasSessions = children.length > 0;
          const isEditingMain = editingMainKey === main.event_key;
          return (
            <Box
              key={main.event_key}
              ref={(node) => setMainRowRef(main.event_key, node)}
              data-cal-main-key={main.event_key}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                border: '2px solid',
                borderColor: isDropTarget ? 'success.main' : (main.is_hidden ? '#f2c97d' : '#e7e7e7'),
                boxShadow: isDropTarget ? '0 0 0 4px rgba(46,125,50,0.12)' : 'none',
                overflow: 'hidden',
                opacity: main.is_hidden ? 0.75 : 1,
                transition: 'border-color 120ms ease, box-shadow 120ms ease, opacity 120ms ease',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: isDropTarget ? '#eef9f0' : '#f7f7f7',
                  borderBottom: '1px solid',
                  borderColor: '#ececec',
                }}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                >
                  <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                    {isEditingMain ? (
                      <Stack spacing={1} sx={{ width: '100%', maxWidth: 560, py: 0.5 }}>
                        <TextField size="small" label="Title" value={mainForm.title} onChange={(e) => setMainForm((f) => ({ ...f, title: e.target.value }))} autoFocus fullWidth />
                        {hasSessions ? (
                          <Typography variant="caption" color="text.secondary">Date &amp; time: {mainDateLabel} — set by the sessions (edit a session to change).</Typography>
                        ) : (
                          <>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              <TextField type="date" size="small" label="Date" value={mainForm.event_date} onChange={(e) => setMainForm((f) => ({ ...f, event_date: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ width: 175 }} />
                              <TextField type="date" size="small" label="End date (optional)" value={mainForm.event_end_date} onChange={(e) => setMainForm((f) => ({ ...f, event_end_date: e.target.value }))} InputLabelProps={{ shrink: true }} sx={{ width: 200 }} />
                            </Stack>
                            <TextField size="small" label="Time (free text)" placeholder="e.g. 6:00 PM to 8:00 PM" value={mainForm.event_time} onChange={(e) => setMainForm((f) => ({ ...f, event_time: e.target.value }))} fullWidth />
                          </>
                        )}
                        <TextField size="small" label="Event page link (optional)" value={mainForm.event_page_link} onChange={(e) => setMainForm((f) => ({ ...f, event_page_link: e.target.value }))} fullWidth />
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant="contained" onClick={() => saveMainEdit(main, hasSessions)} disabled={saving}>Save</Button>
                          <Button size="small" onClick={cancelMainEdit} disabled={saving}>Cancel</Button>
                        </Stack>
                      </Stack>
                    ) : (
                    <>
                    {/* No decorative star before the title: the ONLY star in this row is the
                        major-event toggle on the right, so a second one just made staff wonder
                        which star meant what. */}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {main.title}
                      </Typography>
                      {main.product_id && productHref ? (
                        <IconButton
                          component="button"
                          size="small"
                          onClick={(e) => openProductLink(e, productHref)}
                          sx={{
                            color: 'primary.main',
                            p: 0.25,
                            border: '1px solid',
                            borderColor: 'primary.light',
                            bgcolor: '#eef4ff',
                            '&:hover': {
                              bgcolor: '#e1ecff',
                            },
                          }}
                          aria-label={`Open product page for ${main.product_name || `product ${main.product_id}`}`}
                        >
                          <LinkIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      ) : null}
                      {main.is_hidden ? (
                        <Chip size="small" label="Hidden" color="warning" variant="outlined" />
                      ) : null}
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" color="text.secondary">
                        {mainDateLabel}{main.event_time ? ` • ${main.event_time}` : ''}
                      </Typography>
                      {isMultiDay ? (
                        <Chip size="small" label="Multi-day" variant="outlined" color="info" />
                      ) : null}
                    </Stack>
                    {main.product_id ? (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          width: 'fit-content',
                          maxWidth: '100%',
                          px: 1,
                          py: 0.45,
                          borderRadius: 999,
                          bgcolor: '#e8f5e9',
                          color: 'success.dark',
                          border: '1px solid #a5d6a7',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Mapped: {main.product_name || `Product #${main.product_id}`} (#{main.product_id})
                          {main.product_sku ? ` • ${main.product_sku}` : ''}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No WooCommerce product mapped yet
                      </Typography>
                    )}
                    </>
                    )}
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    {isEditingMain ? null : (
                      <Tooltip title="Edit event (title, date, time)">
                        <IconButton size="small" onClick={() => startMainEdit(main)} disabled={saving} aria-label={`Edit ${main.title}`}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <ChannelStatusRow scheduled={channelMap[main.event_key]} yodeck={yodeckMap[main.event_key]} />
                    <Tooltip title={main.is_major_event ? 'Major event — announced ~2 months early on Yodeck + featured in the weekly digest. Click to unflag.' : 'Flag as a MAJOR event — pushed ~2 months early to Yodeck and featured in the weekly digest'}>
                      <IconButton size="small" onClick={() => onToggleMajor(main)} disabled={saving} aria-label={main.is_major_event ? `Unflag ${main.title} as major event` : `Flag ${main.title} as major event`}>
                        {/* Bigger than the neighbouring icons on purpose — it is now the only star
                            in the row and carries real meaning (Yodeck lead time + digest feature). */}
                        {main.is_major_event
                          ? <StarIcon sx={{ fontSize: 26, color: '#e8821e' }} />
                          : <StarBorderIcon sx={{ fontSize: 26 }} />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={main.is_hidden ? 'Hidden (e.g. cancelled) — click to show on portal' : 'Visible on portal — click to hide (use for cancelled/postponed events)'}>
                      <Switch
                        size="small"
                        color="success"
                        checked={!main.is_hidden}
                        disabled={saving}
                        onChange={() => onToggleHidden(main)}
                        inputProps={{ 'aria-label': main.is_hidden ? `Show ${main.title}` : `Hide ${main.title}` }}
                      />
                    </Tooltip>
                    <Button
                      size="small"
                      variant="outlined"
                      endIcon={<KeyboardArrowDownIcon />}
                      onClick={(e) => openActions(e, main)}
                      disabled={saving}
                    >
                      Actions
                    </Button>
                  </Stack>
                </Stack>
              </Box>

              <Box sx={{ px: 1.5, py: 1.25 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: expandedSessions[main.event_key] ? 1 : 0, flexWrap: 'wrap' }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    onClick={() => toggleSessions(main.event_key)}
                    sx={{ px: 0.5, cursor: 'pointer', width: 'fit-content', userSelect: 'none' }}
                  >
                    {expandedSessions[main.event_key]
                      ? <KeyboardArrowDownIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                      : <KeyboardArrowRightIcon sx={{ fontSize: 22, color: 'text.secondary' }} />}
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Sessions{children.length ? ` (${children.length})` : ''}
                    </Typography>
                  </Stack>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => openAddDialog({ parent: main })} disabled={saving} sx={{ py: 0 }}>
                    Add session
                  </Button>
                </Stack>
                {expandedSessions[main.event_key] ? (
                  <Stack spacing={1}>
                    {children.length === 0 ? (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          ml: { xs: 0, md: 3 },
                          borderRadius: 2,
                          border: '1px dashed #d8d8d8',
                          color: 'text.secondary',
                        }}
                      >
                        <Typography variant="body2">Drop a session here.</Typography>
                      </Box>
                    ) : null}

                    {children.map((row) => renderSessionNode(row))}
                  </Stack>
                ) : null}
              </Box>
            </Box>
          );
        })}

        {groupedRows.ungrouped.length > 0 ? (
          <Box sx={{ bgcolor: '#fff8e1', border: '1px solid #f2d488', borderRadius: 3, p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Ungrouped Sessions
            </Typography>
            <Stack spacing={1}>
              {groupedRows.ungrouped.map((row) => renderSessionNode(row))}
            </Stack>
          </Box>
        ) : null}
      </Stack>
      {dragPreview ? (
        <Box
          sx={{
            position: 'fixed',
            left: dragPreview.x + 14,
            top: dragPreview.y + 14,
            zIndex: 1500,
            pointerEvents: 'none',
            px: 1.25,
            py: 0.75,
            borderRadius: 1.5,
            bgcolor: 'success.main',
            color: 'success.contrastText',
            boxShadow: 6,
            maxWidth: 280,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {dragPreview.title}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.92 }}>
            Drop on a main event row
          </Typography>
        </Box>
      ) : null}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Map Product</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Main event</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedEvent?.title || ''}</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedEvent?.event_date ? formatEventDate(selectedEvent.event_date) : ''}{selectedEvent?.event_time ? ` • ${selectedEvent.event_time}` : ''}
              </Typography>
            </Box>
            <Autocomplete
              options={products}
              value={selectedProduct}
              onChange={(_, value) => setSelectedProduct(value)}
              inputValue={productInput}
              onInputChange={(_, value) => setProductInput(value)}
              getOptionLabel={(option) => option?.name ? option.name : ''}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              renderInput={(params) => (
                <TextField {...params} label="Product" placeholder="Type product name or SKU" size="small" />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {selectedEvent?.product_id ? (
            <Button color="error" onClick={() => onDelete(selectedEvent)} disabled={saving}>
              Remove mapping
            </Button>
          ) : null}
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save mapping'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moveDialogOpen} onClose={closeMoveDialog} fullWidth maxWidth="sm">
        <DialogTitle>Move To Main Event</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {moveEvent?.is_primary_event ? 'Main event to convert and move' : 'Session to move'}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {moveEvent?.title || ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {moveEvent?.event_date ? formatEventDate(moveEvent.event_date) : ''}{moveEvent?.event_time ? ` • ${moveEvent.event_time}` : ''}
              </Typography>
            </Box>
            <Autocomplete
              options={moveTargetOptions}
              value={moveTarget}
              onChange={(_, value) => setMoveTarget(value)}
              getOptionLabel={(option) => option?.title ? `${option.title} (${formatEventDate(option.event_date)})` : ''}
              isOptionEqualToValue={(option, value) => String(option?.event_key) === String(value?.event_key)}
              renderInput={(params) => (
                <TextField {...params} label="Move under" placeholder="Choose main event" size="small" />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMoveDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={onMoveToMainEvent} disabled={saving}>
            {saving ? 'Moving...' : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={closeEditDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {editEvent?.event_date ? formatEventDate(editEvent.event_date) : ''}{editEvent?.event_day ? ` • ${editEvent.event_day}` : ''}
              </Typography>
            </Box>
            <TextField
              label="Title"
              size="small"
              value={editForm.title}
              onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Date"
              size="small"
              type="date"
              value={editForm.event_date}
              onChange={(e) => setEditForm((f) => ({ ...f, event_date: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Reschedule by changing the date — the weekday and ordering update automatically."
            />
            {editEvent?.is_primary_event ? (
              <TextField
                label="End date (optional — for multi-day events)"
                size="small"
                type="date"
                value={editForm.event_end_date}
                onChange={(e) => setEditForm((f) => ({ ...f, event_end_date: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: editForm.event_date || undefined }}
                helperText="Leave blank for a single-day event. Set the last day to show a date range."
              />
            ) : null}
            <TextField
              label="Time"
              size="small"
              value={editForm.event_time}
              onChange={(e) => setEditForm((f) => ({ ...f, event_time: e.target.value }))}
              fullWidth
              placeholder="e.g. 9:00 AM, or leave blank"
              helperText="Free-form display text (e.g. '6:00 PM to 8:00 PM'). Leave blank to clear."
            />
            <TextField
              label="Event page link"
              size="small"
              type="url"
              value={editForm.event_page_link}
              onChange={(e) => setEditForm((f) => ({ ...f, event_page_link: e.target.value }))}
              fullWidth
              placeholder="https://example.com/event-page"
              helperText="Optional public event page URL. Leave blank to clear."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={onSaveEdit} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addDialogOpen} onClose={closeAddDialog} fullWidth maxWidth="md">
        <DialogTitle>{addForm.is_primary_event ? 'Add Event' : 'Add Session'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!addForm.parentLocked ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">Type:</Typography>
                <Tooltip title={addForm.is_primary_event ? 'Main event (can hold sessions and a product mapping)' : 'Session (nests under a main event)'}>
                  <Switch
                    color="success"
                    checked={addForm.is_primary_event}
                    onChange={(e) => setAddForm((f) => ({ ...f, is_primary_event: e.target.checked, parent: e.target.checked ? null : f.parent }))}
                    inputProps={{ 'aria-label': 'Toggle main event or session' }}
                  />
                </Tooltip>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {addForm.is_primary_event ? 'Main event' : 'Session'}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Session under <strong>{addForm.parent?.title}</strong> ({addForm.parent?.event_date ? formatEventDate(addForm.parent.event_date) : ''})
              </Typography>
            )}

            {!addForm.is_primary_event && !addForm.parentLocked ? (
              <Autocomplete
                options={mainEventOptions}
                value={addForm.parent}
                onChange={(_, value) => setAddForm((f) => ({ ...f, parent: value }))}
                getOptionLabel={(option) => (option?.title ? `${option.title} (${formatEventDate(option.event_date)})` : '')}
                isOptionEqualToValue={(option, value) => String(option?.event_key) === String(value?.event_key)}
                renderInput={(params) => (
                  <TextField {...params} label="Nest under main event" placeholder="Choose main event" size="small" required />
                )}
              />
            ) : null}

            <TextField
              label="Title"
              size="small"
              value={addForm.title}
              onChange={(e) => setAddForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              autoFocus
              required
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Date"
                size="small"
                type="date"
                value={addForm.event_date}
                onChange={(e) => setAddForm((f) => ({ ...f, event_date: e.target.value }))}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Time"
                size="small"
                value={addForm.event_time}
                onChange={(e) => setAddForm((f) => ({ ...f, event_time: e.target.value }))}
                fullWidth
                placeholder="e.g. 6:00 PM, or leave blank"
                helperText="Free-form display text."
              />
            </Stack>

            {addForm.is_primary_event ? (
              <>
                <TextField
                  label="End date (optional — for multi-day events)"
                  size="small"
                  type="date"
                  value={addForm.event_end_date}
                  onChange={(e) => setAddForm((f) => ({ ...f, event_end_date: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: addForm.event_date || undefined }}
                  helperText="Leave blank for a single-day event. Set the last day to show a date range."
                />
                <TextField
                  label="Event page link"
                  size="small"
                  type="url"
                  value={addForm.event_page_link}
                  onChange={(e) => setAddForm((f) => ({ ...f, event_page_link: e.target.value }))}
                  fullWidth
                  placeholder="https://example.com/event-page"
                  helperText="Optional public event page URL."
                />
                <Autocomplete
                  options={addProducts}
                  value={addForm.product}
                  onChange={(_, value) => setAddForm((f) => ({ ...f, product: value }))}
                  inputValue={addProductInput}
                  onInputChange={(_, value) => setAddProductInput(value)}
                  getOptionLabel={(option) => (option?.name ? option.name : '')}
                  isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                  renderInput={(params) => (
                    <TextField {...params} label="Mapped product" placeholder="Type product name or SKU" size="small"
                      helperText="Optional WooCommerce product. Sessions inherit it automatically." />
                  )}
                />
                <TextField
                  label="Description (newsletter 'Importance of the Festival/Event')"
                  size="small"
                  multiline
                  minRows={3}
                  value={addForm.description}
                  onChange={(e) => setAddForm((f) => ({ ...f, description: e.target.value }))}
                  fullWidth
                  helperText="Used in the event email. You can also edit it later from the Email dialog."
                />

                <Divider />
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Sessions</Typography>
                  <Button size="small" variant="outlined" onClick={addSessionRow}>+ Add session</Button>
                </Stack>
                {addForm.sessions.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    Optional. Add sub-events (e.g. Homam, Kalyanam) — they nest under this event and inherit its product.
                  </Typography>
                ) : null}
                {addForm.sessions.map((s, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      label="Session title" size="small" value={s.title}
                      onChange={(e) => updateSessionRow(idx, { title: e.target.value })}
                      sx={{ flex: 2 }} required
                    />
                    <TextField
                      label="Date" size="small" type="date" value={s.event_date}
                      onChange={(e) => updateSessionRow(idx, { event_date: e.target.value })}
                      InputLabelProps={{ shrink: true }} sx={{ flex: 1.4 }}
                    />
                    <TextField
                      label="Time" size="small" value={s.event_time}
                      onChange={(e) => updateSessionRow(idx, { event_time: e.target.value })}
                      placeholder="e.g. 9:30 AM" sx={{ flex: 1.4 }}
                    />
                    <Tooltip title="Remove session">
                      <IconButton size="small" color="error" onClick={() => removeSessionRow(idx)} sx={{ mt: 0.5 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ))}
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={onCreateEvent} disabled={saving}>
            {saving ? 'Creating...' : (addForm.is_primary_event ? 'Create event' : 'Create session')}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={closeActions}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => runAction(() => openMapDialog(actionsRow))}>
          {actionsRow?.product_id ? 'Edit product mapping' : 'Map product'}
        </MenuItem>
        <MenuItem onClick={() => runAction(() => setEmailEvent(actionsRow))}>Event Marketing</MenuItem>
        <MenuItem onClick={() => runAction(() => setSponsorReqEvent(actionsRow))}>Sponsorship Request</MenuItem>
        <MenuItem onClick={() => runAction(() => downloadPoster(actionsRow))} disabled={posterBusy === actionsRow?.event_key}>
          {posterBusy === actionsRow?.event_key ? 'Generating poster…' : 'Download poster image'}
        </MenuItem>
        {actionsRow?.original_is_primary_event ? (
          <MenuItem onClick={() => runAction(() => onConvertToSession(actionsRow))}>Convert to session</MenuItem>
        ) : null}
        {actionsRow?.original_is_primary_event ? (
          <MenuItem onClick={() => runAction(() => openMoveDialog(actionsRow))}>Move to…</MenuItem>
        ) : null}
        <Divider />
        <MenuItem onClick={() => runAction(() => onDeleteEvent(actionsRow))} sx={{ color: 'error.main' }}>Delete event</MenuItem>
      </Menu>

      <EventEmailDialog open={Boolean(emailEvent)} event={emailEvent} onClose={() => setEmailEvent(null)} />
      <SponsorshipRequestDialog open={Boolean(sponsorReqEvent)} event={sponsorReqEvent} onClose={() => setSponsorReqEvent(null)} />
    </PageLayout>
  );
}
